/**
 * SOSHE Store Locator — front-end engine
 * ======================================
 * Self-contained. No build step. Works in a Shopify theme section OR a standalone page.
 * Depends (loaded lazily from CDN): Mapbox GL JS v3 + Mapbox Search JS v1.
 *
 * Usage:
 *   SosheStoreLocator.mount(rootElement, config);
 *
 * Security: all sheet-sourced text is inserted via textContent (never innerHTML);
 * URLs are validated to http/https before use. The token in config is a PUBLIC
 * Mapbox token, expected to be domain-restricted in the Mapbox account.
 */
(function (global) {
  'use strict';

  var MAPBOX_GL_JS  = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js';
  var MAPBOX_GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css';
  var MAPBOX_SEARCH = 'https://api.mapbox.com/search-js/v1.0.0/web.js';

  var _scriptPromises = {};

  function loadScript(src) {
    if (_scriptPromises[src]) return _scriptPromises[src];
    _scriptPromises[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = resolve;
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
    return _scriptPromises[src];
  }
  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = href;
    document.head.appendChild(l);
  }
  function preconnect(href) {
    if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'preconnect'; l.href = href; l.crossOrigin = '';
    document.head.appendChild(l);
  }

  /* ---------- helpers ---------- */

  function isFiniteNum(n) { return typeof n === 'number' && isFinite(n); }

  function safeUrl(u) {
    if (!u) return '';
    try {
      var url = new URL(u, global.location ? global.location.href : undefined);
      return (url.protocol === 'http:' || url.protocol === 'https:') ? url.href : '';
    } catch (e) { return ''; }
  }

  function el(tag, opts) {
    var node = document.createElement(tag);
    opts = opts || {};
    if (opts.class) node.className = opts.class;
    if (opts.text != null) node.textContent = String(opts.text);
    if (opts.attrs) Object.keys(opts.attrs).forEach(function (k) { node.setAttribute(k, opts.attrs[k]); });
    if (opts.html === true && opts.text != null) { /* never used; textContent only */ }
    return node;
  }

  // Haversine distance
  function distance(lat1, lon1, lat2, lon2, unit) {
    var R = unit === 'km' ? 6371 : 3958.8; // miles by default
    var toRad = function (d) { return d * Math.PI / 180; };
    var dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatAddress(s) {
    var line1 = [s.address1, s.address2].filter(Boolean).join(', ');
    var line2 = [s.city, s.region].filter(Boolean).join(', ');
    if (s.postalCode) line2 = (line2 ? line2 + ' ' : '') + s.postalCode;
    return [line1, line2].filter(Boolean).join('\n');
  }

  function directionsHref(s) {
    var explicit = safeUrl(s.directionsUrl);
    if (explicit) return explicit;
    var addrParts = [s.address1, s.address2, s.city, s.region, s.postalCode, s.country].filter(Boolean).join(', ');
    var dest = addrParts || (isFiniteNum(s.latitude) ? (s.latitude + ',' + s.longitude) : '');
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
  }

  function debounce(fn, ms) {
    var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }

  // Draws a simple teardrop pin on a canvas and returns ImageData for map.addImage.
  function makePinImage(color) {
    var r = 2, w = 28, h = 38;
    var c = document.createElement('canvas'); c.width = w * r; c.height = h * r;
    var ctx = c.getContext('2d'); ctx.scale(r, r);
    var cx = 14, cy = 13, rad = 10;
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.moveTo(cx, 36); ctx.lineTo(cx - 7, cy + 5); ctx.lineTo(cx + 7, cy + 5); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, rad, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 1.6; ctx.strokeStyle = '#ffffff'; ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, 3.4, 0, Math.PI * 2); ctx.fillStyle = '#ffffff'; ctx.fill();
    return ctx.getImageData(0, 0, w * r, h * r);
  }

  /* ---------- config defaults ---------- */

  var DEFAULTS = {
    mapboxToken: '',
    mapStyle: 'mapbox://styles/mapbox/light-v11',
    storesEndpoint: '',
    defaultCountry: 'us',
    distanceUnit: 'mi',
    heading: 'Find a store near you',
    intro: '',
    mapHeight: 560,
    initialZoom: 3,
    defaultCenter: [-98.5795, 39.8283], // geographic center of contiguous US
    maxFitZoom: 12,
    searchRadius: 40,   // only frame stores within this distance of a search (mi/km)
    searchZoom: 10,     // zoom used when nothing is nearby
    mobilePageSize: 20, // stores shown per page on mobile ("See more" loads another batch)
    loadingLogo: '',    // optional image URL: shown spinning while loading
    markerStyle: 'circle',   // 'circle' | 'pin'  (customer toggle + default)
    showLegend: false,       // legend kept in code but hidden for now
    pinColor: '#072B31',
    selectedColor: '#E87722',
    // "Coming Soon" auto-hides per assortment on/after these dates. Keyed by assortment,
    // so only Ulta (the only stores with an assortment) show it.
    comingSoonUntil: {
      'Full Product Assortment': '2026-08-02',
      'Curated Product Assortment': '2026-08-09'
    },
    comingSoonLabel: 'Coming Soon',
    usBounds: [[-125, 24.5], [-66.5, 49.5]], // continental US framing on load
    retailers: {
      'Ulta':                 { label: 'Ulta',        color: '#E00747', initial: 'U' },
      'Credo':                { label: 'Credo',       color: '#1F6F5C', initial: 'C' },
      'Independent Retailer': { label: 'Independent', color: '#8A5A9E', initial: 'I' },
      'Spa':                  { label: 'Spa',         color: '#2E6FB0', initial: 'P' },
      'Salon':                { label: 'Salon',       color: '#C9781E', initial: 'L' },
      'Other':                { label: 'Other',       color: '#555555', initial: 'O' }
    },
    defaultRetailerStyle: { label: 'Store', color: '#111111', initial: 'S' }
  };

  function displayTags(s) {
    if (Array.isArray(s.displayTypes) && s.displayTypes.length) return s.displayTypes;
    return s.displayType ? [s.displayType] : [];
  }
  function retailerStyle(cfg, name) {
    return (cfg.retailers && cfg.retailers[name]) || cfg.defaultRetailerStyle;
  }

  /* ---------- main class ---------- */

  function SosheStoreLocator(root, config) {
    this.root = root;
    this.cfg = Object.assign({}, DEFAULTS, config || {});
    this.cfg.retailers = Object.assign({}, DEFAULTS.retailers, (config && config.retailers) || {});
    this.stores = [];
    this.filtered = [];
    this.searchPoint = null;      // {lng,lat}
    this.selectedId = null;
    this.map = null;
    this.popup = null;
    this.uid = 'ssl-' + Math.random().toString(36).slice(2, 8);
    this._built = false;
  }

  SosheStoreLocator.mount = function (root, config) {
    if (!root) return null;
    if (root.__ssl) return root.__ssl; // guard: one instance per element
    var inst = new SosheStoreLocator(root, config);
    root.__ssl = inst;
    inst.init();
    return inst;
  };

  var P = SosheStoreLocator.prototype;

  P.init = function () {
    this.buildSkeleton();
    var self = this;
    // Lazy-init the map only when it scrolls into view (perf); list loads immediately.
    if ('IntersectionObserver' in global) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { io.disconnect(); self.boot(); }
        });
      }, { rootMargin: '200px' });
      io.observe(this.root);
    } else {
      this.boot();
    }
  };

  P.boot = function () {
    var self = this;
    this.setStatus('loading');
    this.showListLoading();
    // 1) Data first — the list renders immediately, never waiting on map libraries.
    this.fetchStores().then(function () {
      self.applyFilters();
      self.setStatus('ready');
    }).catch(function (err) {
      console.error('[SosheStoreLocator]', err);
      self.setStatus('error');
      self.showListError();
    });
    // 2) Map library, loaded independently. If it's slow or blocked, the list still works.
    // Warm up the connection so the map assets start downloading sooner.
    preconnect('https://api.mapbox.com');
    // Search autocomplete loads regardless (works from the list, independent of the map).
    loadScript(MAPBOX_SEARCH).then(function () { self.mountSearchBox(); })
      .catch(function () { self.mountSearchBox(); });
    // Map: desktop loads it now; mobile (list is the default view) defers the ~1MB map library
    // until the Map tab is tapped or a search happens. Keyed to screen size + an explicit tap,
    // so desktop always loads and can't break.
    var isMobile = global.matchMedia && global.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) this.loadMap();
  };

  P.loadLibraries = function () {
    loadCss(MAPBOX_GL_CSS);
    return Promise.all([loadScript(MAPBOX_GL_JS), loadScript(MAPBOX_SEARCH)]);
  };

  /* ---------- data ---------- */

  P.fetchStores = function () {
    var self = this;
    if (!this.cfg.storesEndpoint) return Promise.reject(new Error('No storesEndpoint configured'));
    return fetch(this.cfg.storesEndpoint, { cache: 'default' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        if (!json || !Array.isArray(json.stores)) throw new Error('Malformed store data');
        // Keep only rows with valid coordinates (defensive; endpoint already filters).
        self.stores = json.stores.filter(function (s) {
          return isFiniteNum(+s.latitude) && isFiniteNum(+s.longitude);
        }).map(function (s) {
          s.latitude = +s.latitude; s.longitude = +s.longitude; return s;
        });
        self.populateFilterOptions();
      });
  };

  /* ---------- UI skeleton ---------- */

  P.buildSkeleton = function () {
    var cfg = this.cfg, self = this;
    this.root.classList.add('ssl');
    this.root.setAttribute('data-uid', this.uid);
    if (cfg.loadingLogo) { this.root.style.setProperty('--ssl-logo', 'url("' + cfg.loadingLogo + '")'); this.root.classList.add('ssl-has-logo'); }

    var header = el('div', { class: 'ssl-header' });
    if (cfg.heading) header.appendChild(el('h2', { class: 'ssl-heading', text: cfg.heading }));
    if (cfg.intro) header.appendChild(el('p', { class: 'ssl-intro', text: cfg.intro }));

    // Controls
    var controls = el('div', { class: 'ssl-controls' });

    var searchWrap = el('div', { class: 'ssl-search' });
    this.searchMount = el('div', { class: 'ssl-searchbox', attrs: { 'aria-label': 'Search by ZIP, city, state, or address' } });
    searchWrap.appendChild(this.searchMount);
    var geoBtn = el('button', { class: 'ssl-btn ssl-geo', text: 'Use my location', attrs: { type: 'button' } });
    geoBtn.addEventListener('click', function () { self.useMyLocation(); });
    searchWrap.appendChild(geoBtn);
    controls.appendChild(searchWrap);

    // Filters live in a space-saving popover.
    var filterWrap = el('div', { class: 'ssl-filter-wrap' });
    this.filterToggle = el('button', { class: 'ssl-btn ssl-filter-toggle', text: 'Filters', attrs: { type: 'button', 'aria-expanded': 'false', 'aria-haspopup': 'true' } });
    this.filterPanel = el('div', { class: 'ssl-filter-panel', attrs: { role: 'group', 'aria-label': 'Filters' } });
    this.retailerSel = this.buildSelect('Retailer', 'All retailers');
    this.assortSel = this.buildSelect('Assortment', 'All assortments');
    this.filterPanel.appendChild(this.wrapField('Retailer', this.retailerSel));
    this.assortField = this.wrapField('Assortment', this.assortSel);
    this.assortField.style.display = 'none';
    this.filterPanel.appendChild(this.assortField);
    var panelActions = el('div', { class: 'ssl-filter-actions' });
    var clearBtn = el('button', { class: 'ssl-btn ssl-clear', text: 'Clear', attrs: { type: 'button' } });
    clearBtn.addEventListener('click', function () { self.clearFilters(); });
    var doneBtn = el('button', { class: 'ssl-btn ssl-dir ssl-done', text: 'Done', attrs: { type: 'button' } });
    doneBtn.addEventListener('click', function () { self.toggleFilters(false); });
    panelActions.appendChild(clearBtn); panelActions.appendChild(doneBtn);
    this.filterPanel.appendChild(panelActions);
    filterWrap.appendChild(this.filterToggle);
    filterWrap.appendChild(this.filterPanel);
    controls.appendChild(filterWrap);

    this.filterToggle.addEventListener('click', function (e) { e.stopPropagation(); self.toggleFilters(); });
    this.retailerSel.addEventListener('change', function () { self.applyFilters(); });
    this.assortSel.addEventListener('change', function () { self.applyFilters(); });
    document.addEventListener('click', function (e) { if (self.filterPanel && !filterWrap.contains(e.target)) self.toggleFilters(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.toggleFilters(false); });

    // Meta row: results count + mobile toggle
    var meta = el('div', { class: 'ssl-meta' });
    this.countEl = el('span', { class: 'ssl-count', attrs: { 'aria-live': 'polite', role: 'status' }, text: '' });
    meta.appendChild(this.countEl);
    var toggle = el('div', { class: 'ssl-toggle', attrs: { role: 'tablist', 'aria-label': 'List or map view' } });
    this.listTab = el('button', { class: 'ssl-toggle-btn is-active', text: 'List', attrs: { type: 'button', role: 'tab', 'aria-selected': 'true' } });
    this.mapTab = el('button', { class: 'ssl-toggle-btn', text: 'Map', attrs: { type: 'button', role: 'tab', 'aria-selected': 'false' } });
    this.listTab.addEventListener('click', function () { self.setMobileView('list'); });
    this.mapTab.addEventListener('click', function () { self.setMobileView('map'); });
    toggle.appendChild(this.listTab); toggle.appendChild(this.mapTab);
    meta.appendChild(toggle);

    // Body: list + map
    var body = el('div', { class: 'ssl-body' });
    this.listEl = el('div', { class: 'ssl-list', attrs: { role: 'list', 'aria-label': 'Store results', tabindex: '0' } });
    this.mapWrap = el('div', { class: 'ssl-mapwrap' });
    this.mapEl = el('div', { class: 'ssl-map', attrs: { 'aria-label': 'Map of store locations', role: 'application' } });
    this.root.style.setProperty('--ssl-panel-h', ((parseInt(cfg.mapHeight, 10) || 560)) + 'px');
    this.mapWrap.appendChild(this.mapEl);

    // status overlay
    this.statusEl = el('div', { class: 'ssl-status', attrs: { 'aria-live': 'polite' } });
    this.mapWrap.appendChild(this.statusEl);

    body.appendChild(this.listEl);
    body.appendChild(this.mapWrap);

    // legend
    this.legendEl = el('div', { class: 'ssl-legend', attrs: { 'aria-hidden': 'false' } });

    this.root.appendChild(header);
    this.root.appendChild(controls);
    this.root.appendChild(meta);
    if (cfg.showLegend) this.root.appendChild(this.legendEl);
    this.root.appendChild(body);
    this._built = true;

    global.addEventListener('resize', debounce(function () { if (self.map) self.map.resize(); }, 200));
  };

  P.buildSelect = function (id, allLabel) {
    var sel = el('select', { class: 'ssl-select', attrs: { 'aria-label': id } });
    sel.appendChild(el('option', { text: allLabel, attrs: { value: '' } }));
    return sel;
  };
  P.wrapField = function (label, control) {
    var w = el('label', { class: 'ssl-field' });
    w.appendChild(el('span', { class: 'ssl-field-label', text: label }));
    w.appendChild(control);
    return w;
  };
  P.toggleFilters = function (open) {
    if (!this.filterPanel) return;
    var willOpen = (open === undefined) ? !this.filterPanel.classList.contains('is-open') : open;
    this.filterPanel.classList.toggle('is-open', willOpen);
    if (this.filterToggle) this.filterToggle.setAttribute('aria-expanded', String(willOpen));
  };
  P.updateFilterButton = function () {
    var n = 0;
    if (this.retailerSel && this.retailerSel.value) n++;
    if (this.assortSel && this.assortSel.value) n++;
    if (this.filterToggle) this.filterToggle.textContent = n ? ('Filters (' + n + ')') : 'Filters';
  };

  P.populateFilterOptions = function () {
    var retailers = {}, assorts = {};
    this.stores.forEach(function (s) {
      if (s.retailer) retailers[s.retailer] = true;
      if (s.assortment) assorts[s.assortment] = true;
    });
    var fill = function (sel, obj) {
      // reset (keep first "All" option)
      while (sel.options.length > 1) sel.remove(1);
      Object.keys(obj).sort().forEach(function (v) {
        sel.appendChild(el('option', { text: v, attrs: { value: v } }));
      });
    };
    fill(this.retailerSel, retailers);
    fill(this.assortSel, assorts);
    this.assortField.style.display = Object.keys(assorts).length ? '' : 'none';
    this.buildLegend(Object.keys(retailers));
  };

  P.buildLegend = function (retailers) {
    var cfg = this.cfg;
    this.legendEl.textContent = '';
    if (!cfg.showLegend) return; // legend disabled for now (code retained)
    retailers.sort().forEach(function (name) {
      var st = retailerStyle(cfg, name);
      var item = el('span', { class: 'ssl-legend-item' });
      var dot = el('span', { class: 'ssl-legend-dot', text: st.initial });
      dot.style.background = st.color;
      item.appendChild(dot);
      item.appendChild(el('span', { text: st.label }));
      this.legendEl.appendChild(item);
    }, this);
  };

  /* ---------- filtering + sorting ---------- */

  P.applyFilters = function () {
    var rf = this.retailerSel.value, af = this.assortSel ? this.assortSel.value : '', self = this;
    this.filtered = this.stores.filter(function (s) {
      if (rf && s.retailer !== rf) return false;
      if (af && s.assortment !== af) return false;
      return true;
    });
    if (this.searchPoint) {
      this.filtered.forEach(function (s) {
        s._dist = distance(self.searchPoint.lat, self.searchPoint.lng, s.latitude, s.longitude, self.cfg.distanceUnit);
      });
      this.filtered.sort(function (a, b) { return a._dist - b._dist; });
    }
    this.renderList();
    this.updateMapData();
    this.updateCount();
    this.updateFilterButton();
    var interacted = this.searchPoint || this.retailerSel.value || (this.assortSel && this.assortSel.value);
    if (this.map && interacted) this.fitToResults();
  };

  P.clearFilters = function () {
    this.retailerSel.value = '';
    if (this.assortSel) this.assortSel.value = '';
    this.applyFilters();
    if (this.map && !this.searchPoint) this.frameInitial();
  };

  P.updateCount = function () {
    var n = this.filtered.length;
    var unit = this.cfg.distanceUnit === 'km' ? 'km' : 'mi';
    var txt = n + (n === 1 ? ' store' : ' stores');
    if (this.searchPoint) txt += ' near you (sorted by distance in ' + unit + ')';
    this.countEl.textContent = txt;
  };

  /* ---------- list rendering ---------- */

  P.isComingSoon = function (s) {
    var map = this.cfg.comingSoonUntil;
    if (!map || !s.assortment || !map[s.assortment]) return false;
    var until = new Date(map[s.assortment]);
    return !isNaN(until.getTime()) && Date.now() < until.getTime();
  };

  // "Coming Soon - Aug 2" — appends the store's own launch date.
  P.comingSoonText = function (s) {
    var base = this.cfg.comingSoonLabel || 'Coming Soon';
    var map = this.cfg.comingSoonUntil, until = map && map[s.assortment];
    if (!until) return base;
    var p = String(until).split('-'); // yyyy-mm-dd
    if (p.length !== 3) return base;
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var mo = months[parseInt(p[1], 10) - 1] || p[1];
    return base + ' - ' + mo + ' ' + parseInt(p[2], 10);
  };

  P.showListLoading = function () {
    this.listEl.textContent = '';
    var wrap = el('div', { class: 'ssl-loading' });
    wrap.appendChild(el('span', { class: 'ssl-spinner', attrs: { 'aria-hidden': 'true' } }));
    wrap.appendChild(el('span', { text: 'Loading stores\u2026' }));
    this.listEl.appendChild(wrap);
  };

  P.showListError = function () {
    this.listEl.textContent = '';
    var e = el('div', { class: 'ssl-empty' });
    e.appendChild(el('p', { class: 'ssl-empty-title', text: 'Couldn\u2019t load stores' }));
    e.appendChild(el('p', { text: 'Please refresh and try again.' }));
    this.listEl.appendChild(e);
  };

  P.renderList = function () {
    this.listEl.textContent = '';
    if (!this.filtered.length) {
      var empty = el('div', { class: 'ssl-empty' });
      empty.appendChild(el('p', { class: 'ssl-empty-title', text: 'No stores match your search' }));
      empty.appendChild(el('p', { text: 'Try a different ZIP code or city, or clear your filters.' }));
      this.listEl.appendChild(empty);
      return;
    }
    // On mobile show only the first page (nearest-first once searched); everything on desktop.
    var isMobile = global.matchMedia && global.matchMedia('(max-width: 768px)').matches;
    this._pageSize = isMobile ? (this.cfg.mobilePageSize || 20) : this.filtered.length;
    this._shown = Math.min(this._pageSize, this.filtered.length);
    this._renderRange(0, this._shown);
  };

  P._renderRange = function (from, to) {
    var self = this, list = this.filtered, i = from, CHUNK = 40;
    var token = (this._renderToken = (this._renderToken || 0) + 1);
    var raf = global.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    (function chunk() {
      if (token !== self._renderToken) return; // superseded by a newer render
      var frag = document.createDocumentFragment();
      var end = Math.min(i + CHUNK, to);
      for (; i < end; i++) frag.appendChild(self.buildCard(list[i]));
      self.listEl.appendChild(frag);
      if (i < to) raf(chunk); else self._syncLoadMore();
    })();
  };

  P._syncLoadMore = function () {
    var existing = this.listEl.querySelector('.ssl-loadmore');
    if (existing) existing.remove();
    if (this._shown < this.filtered.length) {
      var self = this, remaining = this.filtered.length - this._shown;
      var btn = el('button', { class: 'ssl-btn ssl-loadmore', text: 'See more', attrs: { type: 'button' } });
      btn.addEventListener('click', function () { self.loadMore(); });
      this.listEl.appendChild(btn);
    }
  };

  P.loadMore = function () {
    var from = this._shown;
    this._shown = Math.min(this._shown + this._pageSize, this.filtered.length);
    var b = this.listEl.querySelector('.ssl-loadmore'); if (b) b.remove();
    this._renderRange(from, this._shown);
  };

  P.buildCard = function (s) {
    var self = this, cfg = this.cfg;
    var st = retailerStyle(cfg, s.retailer);
    var card = el('div', { class: 'ssl-card', attrs: { role: 'listitem', tabindex: '0', 'data-id': s.id } });

    var head = el('div', { class: 'ssl-card-head' });
    var badge = el('span', { class: 'ssl-badge', text: st.label });
    badge.style.borderColor = st.color; badge.style.color = st.color;
    head.appendChild(el('h3', { class: 'ssl-card-name', text: s.name }));
    card.appendChild(head);

    var tags = el('div', { class: 'ssl-card-tags' });
    tags.appendChild(badge);
    if (self.isComingSoon(s)) tags.appendChild(el('span', { class: 'ssl-soon', text: self.comingSoonText(s) }));
    if (s.assortment) tags.appendChild(el('span', { class: 'ssl-dtype', text: s.assortment }));
    if (s.isNew) tags.appendChild(el('span', { class: 'ssl-new', text: 'New' }));
    if (isFiniteNum(s._dist)) tags.appendChild(el('span', { class: 'ssl-dist', text: s._dist.toFixed(1) + ' ' + (cfg.distanceUnit === 'km' ? 'km' : 'mi') }));
    card.appendChild(tags);

    var addr = el('p', { class: 'ssl-card-addr' });
    formatAddress(s).split('\n').forEach(function (line, i) {
      if (i) addr.appendChild(document.createElement('br'));
      addr.appendChild(document.createTextNode(line));
    });
    card.appendChild(addr);

    var actions = el('div', { class: 'ssl-card-actions' });
    if (s.phone) {
      var tel = el('a', { class: 'ssl-link', text: s.phone, attrs: { href: 'tel:' + String(s.phone).replace(/[^0-9+]/g, '') } });
      actions.appendChild(tel);
    }
    var web = safeUrl(s.websiteUrl);
    if (web) {
      actions.appendChild(el('a', { class: 'ssl-link', text: 'Visit website', attrs: { href: web, target: '_blank', rel: 'noopener noreferrer' } }));
    }
    var dir = el('a', { class: 'ssl-btn ssl-dir', text: 'Get Directions', attrs: { href: directionsHref(s), target: '_blank', rel: 'noopener noreferrer' } });
    actions.appendChild(dir);
    card.appendChild(actions);

    var select = function () { self.selectStore(s.id, true); };
    card.addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // let links work
      select();
    });
    card.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); }
    });
    return card;
  };

  /* ---------- map ---------- */

  // Create the map only once its container is actually on-screen (has size). Prevents the
  // blank map when it would otherwise boot at 0x0 inside the hidden mobile list panel.
  P.loadMap = function () {
    if (this._mapLoadStarted) return;
    this._mapLoadStarted = true;
    var self = this;
    loadCss(MAPBOX_GL_CSS);
    loadScript(MAPBOX_GL_JS).then(function () { self.initMap(); })
      .catch(function (e) { console.error('[SosheStoreLocator] map library failed', e); });
  };

  P.initMap = function () {
    var mapboxgl = global.mapboxgl;
    if (!mapboxgl) throw new Error('Mapbox GL failed to load');
    mapboxgl.accessToken = this.cfg.mapboxToken;

    this.map = new mapboxgl.Map({
      container: this.mapEl,
      style: this.cfg.mapStyle,
      center: this.cfg.defaultCenter,
      zoom: this.cfg.initialZoom,
      projection: 'mercator',
      cooperativeGestures: true
    });
    var self = this;
    // Keep the canvas matched to its container — fixes the 'map only fills part of the box'
    // problem when the map initializes before layout settles (theme editor, lazy load).
    if ('ResizeObserver' in global) {
      this._ro = new ResizeObserver(function () { if (self.map) self.map.resize(); });
      this._ro.observe(this.mapEl);
    }
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    this.map.on('load', function () {
      self.addLayers();
      self.updateMapData();
      self.map.resize();
      if (self.searchPoint) self.fitToResults(); else self.frameInitial();
      // Safety re-sync after late layout shifts (fonts/images pushing the grid around).
      setTimeout(function () { if (self.map) { self.map.resize(); if (self.searchPoint) self.fitToResults(); else self.frameInitial(); } }, 300);
    });
    this.map.on('error', function (e) {
      console.error('[SosheStoreLocator] map error', e && e.error);
    });
  };

  P.storesGeoJSON = function (list) {
    return {
      type: 'FeatureCollection',
      features: (list || this.filtered).map(function (s) {
        return {
          type: 'Feature',
          properties: { id: s.id, retailer: s.retailer || 'Other' },
          geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] }
        };
      })
    };
  };

  P.addLayers = function () {
    var map = this.map, cfg = this.cfg;
    map.addSource('stores', {
      type: 'geojson', data: this.storesGeoJSON([]),
      cluster: true, clusterMaxZoom: 14, clusterRadius: 50
    });
    map.addSource('selected', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

    // retailer color match expression
    var colorExpr = ['match', ['get', 'retailer']];
    var initialExpr = ['match', ['get', 'retailer']];
    Object.keys(cfg.retailers).forEach(function (name) {
      colorExpr.push(name, cfg.retailers[name].color);
      initialExpr.push(name, cfg.retailers[name].initial);
    });
    colorExpr.push(cfg.defaultRetailerStyle.color);
    initialExpr.push(cfg.defaultRetailerStyle.initial);

    map.addLayer({
      id: 'clusters', type: 'circle', source: 'stores', filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#072B31',
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
        'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff'
      }
    });
    map.addLayer({
      id: 'cluster-count', type: 'symbol', source: 'stores', filter: ['has', 'point_count'],
      layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 13,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'] },
      paint: { 'text-color': '#ffffff' }
    });
    map.addLayer({
      id: 'unclustered', type: 'circle', source: 'stores', filter: ['!', ['has', 'point_count']],
      layout: { visibility: cfg.markerStyle === 'pin' ? 'none' : 'visible' },
      paint: {
        'circle-color': '#072B31', 'circle-radius': 11,
        'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff'
      }
    });
    // Pin style (teardrop) as an alternate to circles — toggled at runtime.
    if (!map.hasImage('ssl-pin')) { try { map.addImage('ssl-pin', makePinImage(cfg.pinColor || '#111111'), { pixelRatio: 2 }); } catch (e) {} }
    map.addLayer({
      id: 'unclustered-pin', type: 'symbol', source: 'stores', filter: ['!', ['has', 'point_count']],
      layout: { 'icon-image': 'ssl-pin', 'icon-size': 0.85, 'icon-anchor': 'bottom', 'icon-allow-overlap': true,
        visibility: cfg.markerStyle === 'pin' ? 'visible' : 'none' }
    });
    // selected highlight ring
    map.addLayer({
      id: 'selected-ring', type: 'circle', source: 'selected',
      paint: {
        'circle-radius': 13, 'circle-color': (cfg.selectedColor || '#E87722'),
        'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff'
      }
    });

    // Orange pin marking the searched location (ZIP / address / current location).
    map.addSource('searchpt', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    if (!map.hasImage('ssl-search-pin')) { try { map.addImage('ssl-search-pin', makePinImage(cfg.selectedColor || '#E87722'), { pixelRatio: 2 }); } catch (e) {} }
    map.addLayer({
      id: 'search-point', type: 'symbol', source: 'searchpt',
      layout: { 'icon-image': 'ssl-search-pin', 'icon-size': 1, 'icon-anchor': 'bottom', 'icon-allow-overlap': true, 'icon-ignore-placement': true }
    });
    this.updateSearchMarker();

    var self = this;
    map.on('click', 'clusters', function (e) {
      var f = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
      var cid = f.properties.cluster_id;
      map.getSource('stores').getClusterExpansionZoom(cid, function (err, zoom) {
        if (err) return;
        map.easeTo({ center: f.geometry.coordinates, zoom: zoom });
      });
    });
    ['unclustered', 'unclustered-pin'].forEach(function (layer) {
      map.on('click', layer, function (e) { self.selectStore(e.features[0].properties.id, false); });
    });
    ['clusters', 'unclustered', 'unclustered-pin'].forEach(function (layer) {
      map.on('mouseenter', layer, function () { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layer, function () { map.getCanvas().style.cursor = ''; });
    });
  };

  P.updateMapData = function () {
    if (!this.map || !this.map.getSource('stores')) return;
    this.map.getSource('stores').setData(this.storesGeoJSON());
  };

  P.fitToResults = function () {
    if (!this.map) return;
    var mapboxgl = global.mapboxgl;
    var list = this.filtered.length ? this.filtered : this.stores;
    if (!list.length) return;
    // On a search: zoom INTO the searched area with the nearest few results (list is already
    // sorted by distance), rather than fitting every store nationwide.
    if (this.searchPoint) {
      var radius = this.cfg.searchRadius || 40;
      var near = list.filter(function (s) { return isFiniteNum(s._dist) && s._dist <= radius; }).slice(0, 8);
      // If nothing is within the radius, still show the single nearest store (even if far).
      if (!near.length && list.length) near = list.slice(0, 1);
      if (near.length) {
        var bs = new mapboxgl.LngLatBounds();
        bs.extend([this.searchPoint.lng, this.searchPoint.lat]);
        near.forEach(function (s) { bs.extend([s.longitude, s.latitude]); });
        this.map.fitBounds(bs, { padding: 70, maxZoom: 13, duration: 700 });
      } else {
        // No stores at all in the current results — just center on the searched place.
        this.map.easeTo({ center: [this.searchPoint.lng, this.searchPoint.lat], zoom: (this.cfg.searchZoom || 10), duration: 700 });
      }
      return;
    }
    if (list.length === 1) { this.map.easeTo({ center: [list[0].longitude, list[0].latitude], zoom: 12 }); return; }
    var b = new mapboxgl.LngLatBounds();
    list.forEach(function (s) { b.extend([s.longitude, s.latitude]); });
    this.map.fitBounds(b, { padding: 60, maxZoom: this.cfg.maxFitZoom, duration: 600 });
  };

  // Default opening view: frame the continental US (flat), not a globe fit to every pin.
  P.frameInitial = function () {
    if (!this.map) return;
    var b = this.cfg.usBounds;
    if (b && b.length === 2) this.map.fitBounds(b, { padding: 20, duration: 0 });
  };

  P.setMarkerStyle = function (style) {
    this.cfg.markerStyle = (style === 'pin') ? 'pin' : 'circle';
    if (this.markerToggle) this.markerToggle.textContent = this.cfg.markerStyle === 'pin' ? 'Show circles' : 'Show pins';
    var m = this.map;
    if (m && m.getLayer && m.getLayer('unclustered')) {
      var pin = this.cfg.markerStyle === 'pin';
      m.setLayoutProperty('unclustered', 'visibility', pin ? 'none' : 'visible');
      m.setLayoutProperty('unclustered-pin', 'visibility', pin ? 'visible' : 'none');
    }
  };

  P.selectStore = function (id, fromCard) {
    var s = this.findStore(id);
    if (!s) return;
    this.selectedId = id;

    // highlight ring on map
    if (this.map && this.map.getSource('selected')) {
      this.map.getSource('selected').setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] } }]
      });
      this.map.flyTo({ center: [s.longitude, s.latitude], zoom: Math.max(this.map.getZoom(), 13), duration: 600 });
      this.openPopup(s);
    }
    // sync card highlight
    var cards = this.listEl.querySelectorAll('.ssl-card');
    cards.forEach(function (c) {
      var on = c.getAttribute('data-id') === id;
      c.classList.toggle('is-selected', on);
      if (on && !fromCard) c.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  P.openPopup = function (s) {
    var mapboxgl = global.mapboxgl;
    if (this.popup) this.popup.remove();
    var content = this.buildPopup(s);
    this.popup = new mapboxgl.Popup({ offset: 16, closeButton: true, maxWidth: '280px' })
      .setLngLat([s.longitude, s.latitude])
      .setDOMContent(content)
      .addTo(this.map);
  };

  P.buildPopup = function (s) {
    var cfg = this.cfg, st = retailerStyle(cfg, s.retailer);
    var wrap = el('div', { class: 'ssl-popup' });
    wrap.appendChild(el('strong', { class: 'ssl-popup-name', text: s.name }));
    var badge = el('span', { class: 'ssl-badge', text: st.label });
    badge.style.borderColor = st.color; badge.style.color = st.color;
    var tagRow = el('div', { class: 'ssl-card-tags' });
    tagRow.appendChild(badge);
    if (this.isComingSoon(s)) tagRow.appendChild(el('span', { class: 'ssl-soon', text: this.comingSoonText(s) }));
    if (s.assortment) tagRow.appendChild(el('span', { class: 'ssl-dtype', text: s.assortment }));
    if (s.isNew) tagRow.appendChild(el('span', { class: 'ssl-new', text: 'New' }));
    wrap.appendChild(tagRow);
    var addr = el('p', { class: 'ssl-card-addr' });
    formatAddress(s).split('\n').forEach(function (line, i) {
      if (i) addr.appendChild(document.createElement('br'));
      addr.appendChild(document.createTextNode(line));
    });
    wrap.appendChild(addr);
    if (isFiniteNum(s._dist)) wrap.appendChild(el('p', { class: 'ssl-dist', text: s._dist.toFixed(1) + ' ' + (cfg.distanceUnit === 'km' ? 'km' : 'mi') + ' away' }));
    var dir = el('a', { class: 'ssl-btn ssl-dir', text: 'Get Directions', attrs: { href: directionsHref(s), target: '_blank', rel: 'noopener noreferrer' } });
    wrap.appendChild(dir);
    return wrap;
  };

  P.findStore = function (id) {
    for (var i = 0; i < this.stores.length; i++) if (this.stores[i].id === id) return this.stores[i];
    return null;
  };

  /* ---------- search ---------- */

  // Mapbox Search autocomplete. Mounted after Search JS loads (independent of the map), so the
  // suggestions box is available immediately, including on mobile. Selecting a suggestion fires
  // 'retrieve'. If Search JS is unavailable, falls back to a plain input that geocodes on Enter.
  P.mountSearchBox = function () {
    if (this._searchMounted) return;
    var self = this, cfg = this.cfg, ms = global.mapboxsearch;
    if (ms && ms.MapboxSearchBox) {
      var box = new ms.MapboxSearchBox();
      box.accessToken = cfg.mapboxToken;
      box.options = { country: cfg.defaultCountry, language: 'en', types: 'address,postcode,place,locality,region,neighborhood,district' };
      box.placeholder = 'Search ZIP, city, state, or address';
      box.marker = false;
      // Match the storefront: inherit the page font (Apercu), slate text, our radius/border,
      // and size the input to the shared control height.
      box.theme = {
        variables: {
          fontFamily: 'inherit',
          unit: '16px',
          padding: '0.4em',
          lineHeight: '1.2',
          borderRadius: '10px',
          border: '1px solid #e4e0db',
          boxShadow: 'none',
          colorText: '#072B31'
        },
        // Force the shadow-DOM input to the shared control height so it matches the buttons.
        cssText: 'input { height: 48px !important; box-sizing: border-box; } [class*="Input"] { height: 48px !important; min-height: 48px !important; }'
      };
      this.searchMount.appendChild(box);
      box.addEventListener('retrieve', function (ev) {
        var f = ev.detail && ev.detail.features && ev.detail.features[0];
        if (!f) return;
        var c = f.geometry.coordinates; // [lng, lat]
        self.setSearchPoint(c[0], c[1]);
      });
      this._searchMounted = true;
      return;
    }
    // Fallback: plain input (no button); geocode on Enter.
    var input = el('input', { class: 'ssl-input', attrs: { type: 'search', placeholder: 'Search ZIP, city, state, or address', 'aria-label': 'Search location', enterkeyhint: 'search' } });
    this.searchMount.appendChild(input);
    input.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter') return; ev.preventDefault();
      var q = input.value.trim(); if (!q) return;
      var url = 'https://api.mapbox.com/search/geocode/v6/forward?q=' + encodeURIComponent(q) +
        '&limit=1&country=' + encodeURIComponent(cfg.defaultCountry) + '&access_token=' + encodeURIComponent(cfg.mapboxToken);
      fetch(url).then(function (r) { return r.json(); }).then(function (j) {
        var f = j.features && j.features[0]; if (f) self.setSearchPoint(f.geometry.coordinates[0], f.geometry.coordinates[1]);
      }).catch(function () {});
    });
    this._searchMounted = true;
  };

  P.updateSearchMarker = function () {
    if (!this.map || !this.map.getSource('searchpt')) return;
    var feats = this.searchPoint
      ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: [this.searchPoint.lng, this.searchPoint.lat] } }]
      : [];
    this.map.getSource('searchpt').setData({ type: 'FeatureCollection', features: feats });
  };

  P.setSearchPoint = function (lng, lat) {
    this.searchPoint = { lng: lng, lat: lat };
    this.applyFilters();
    this.updateSearchMarker();
    this.setMobileView('map');
  };

  P.useMyLocation = function () {
    var self = this;
    if (!global.navigator || !navigator.geolocation) { alert('Location is not available in this browser.'); return; }
    // Only requested on explicit click (never on load).
    navigator.geolocation.getCurrentPosition(function (pos) {
      self.setSearchPoint(pos.coords.longitude, pos.coords.latitude);
    }, function () {
      alert('We could not access your location. Please search by ZIP or city instead.');
    }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 });
  };

  /* ---------- view state ---------- */

  P.setMobileView = function (view) {
    this.root.setAttribute('data-view', view);
    var listOn = view === 'list';
    this.listTab.classList.toggle('is-active', listOn);
    this.mapTab.classList.toggle('is-active', !listOn);
    this.listTab.setAttribute('aria-selected', String(listOn));
    this.mapTab.setAttribute('aria-selected', String(!listOn));
    if (!listOn) {
      var self = this;
      this.loadMap();
      setTimeout(function () { if (self.map) { self.map.resize(); if (self.searchPoint) self.fitToResults(); else self.frameInitial(); } }, 120);
      setTimeout(function () { if (self.map) self.map.resize(); }, 400);
    }
  };

  P.setStatus = function (state) {
    var s = this.statusEl;
    s.textContent = '';
    s.setAttribute('data-state', state);
    if (state === 'loading') { s.appendChild(el('span', { class: 'ssl-spinner', attrs: { 'aria-hidden': 'true' } })); s.appendChild(el('span', { text: '\u00a0Loading map\u2026' })); }
    else if (state === 'error') s.textContent = 'We couldn\u2019t load store locations right now. Please try again later.';
    s.style.display = (state === 'loading' || state === 'error') ? 'flex' : 'none';
  };

  global.SosheStoreLocator = SosheStoreLocator;

  // Auto-mount any standalone containers that carry inline config via data-config.
  document.addEventListener('DOMContentLoaded', function () {
    var nodes = document.querySelectorAll('[data-soshe-locator]');
    nodes.forEach(function (node) {
      if (node.__ssl) return;
      var cfg = {};
      var raw = node.getAttribute('data-config');
      if (raw) { try { cfg = JSON.parse(raw); } catch (e) {} }
      else if (global.SOSHE_STORE_LOCATOR_CONFIG) { cfg = global.SOSHE_STORE_LOCATOR_CONFIG; }
      SosheStoreLocator.mount(node, cfg);
    });
  });

})(typeof window !== 'undefined' ? window : this);